// Per-message renderer — replaces the deleted MessageBubble.tsx. Decomposed,
// not ported: plain text/markdown rendering stays minimal (MarkdownText),
// debug/export/feedback live in one registered ActionBar (reading
// message.metadata.custom) instead of 8 props threaded into one component,
// and run-status (conflict/timedOut/cancelled/error/scheduled) is its own
// concern (RunStatusBadge) rather than being baked into the bubble.

import { createContext, memo, useCallback, useContext, useEffect, useRef, useState, type FC, type ReactNode } from 'react';
import { Children } from 'react';
import { createPortal } from 'react-dom';
import { ActionBarPrimitive, AttachmentPrimitive, AuiIf, MessagePartPrimitive, MessagePrimitive, groupPartByType, useAuiState } from '@assistant-ui/react';
import type { DataMessagePart, ThreadAssistantMessagePart, ToolCallMessagePartComponent } from '@assistant-ui/react';
import { AlertCircleIcon, CheckIcon, ChevronDownIcon, ClipboardIcon, LoaderIcon, Sparkle, XCircleIcon } from 'lucide-react';
import { CollapsibleTrigger } from '@/shared/ui/collapsible';
import { Marker, MarkerContent, MarkerIcon } from '@/shared/ui/marker';
import { Button } from '@/shared/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { useSpecialistDialogStore } from '@/shared/stores/specialistDialog';
import { useInViewOnce } from '@/shared/lib/hooks/useInViewOnce';
import { AttachmentPreviewDialog, pickFileTypeIcon, useAuthenticatedObjectUrl, useFilePreview } from '@/entities/file';
import { MarkdownText, UserMarkdownText } from './MarkdownText';
import { AssistantFilePart } from './AssistantFilePart';
import { AuthenticatedImagePart } from './AuthenticatedImagePart';
import { VegaLiteChart } from './VegaLiteChart';
import { Mermaid } from './Mermaid';
import { Svg } from './Svg';
import { ActionBar } from './ActionBar';
import { RunStatusBadge } from './RunStatusBadge';
import type { SubAgentMessage } from '../model/subAgentResult';
import { summarizeSpecialistAnswer } from '../model/specialistSummary';
import { QuoteBlock } from '@/shared/ui/quote';
import { Sources } from '@/shared/ui/sources';
import { Reasoning, ReasoningRoot, ReasoningTrigger, ReasoningContent, ReasoningText } from '@/shared/ui/reasoning';
import { ToolGroupRoot, ToolGroupTrigger, ToolGroupContent } from '@/shared/ui/tool-group';
import { ToolFallback } from '@/shared/ui/tool-fallback';
import { MessageTimestamp } from '@/shared/ui/message-timing';
import { InterruptPanel } from '@/features/chat/user-question/ui/InterruptPanel';
import { useAgenthubInterrupts } from '@/shared/chat-runtime/agenthubInterrupts';
import { specialistCardAnchorId } from '@/shared/chat-runtime/specialistCardAnchor';

// The registry is the frontend half of the MCP artifact contract. Components
// are declarative renderers; the backend chooses when to call the artifact
// tool and supplies the validated props.
const generativeUiComponents = { VegaLiteChart, Mermaid, Svg };
const MessageRunContext = createContext<boolean | undefined>(undefined);
const SpecialistTaskLabelContext = createContext('Specialist task');
// The specialist's own ActionBar must read the specialist's message context
// (established inside MessagePartPrimitive.Messages), but needs to render
// visually in the dialog's header row alongside the specialist name -- a
// portal bridges the two: SubAgentMessage renders ActionBar (correct
// context) into this DOM node, which AgentToolCard places in its header.
const SpecialistActionBarSlotContext = createContext<HTMLDivElement | null>(null);

// Renders each attachment on a sent user message — mirrors SendComposer's
// AttachmentChip (same AttachmentPrimitive context, just read-only: no
// remove button, no upload-progress state since these are already sent).
function MessageAttachmentChip() {
  const name = useAuiState((s) => s.attachment.name);
  const contentType = useAuiState((s) => s.attachment.contentType);
  const Icon = pickFileTypeIcon(contentType, name);
  return (
    <AttachmentPrimitive.Root className="flex items-center gap-1.5 rounded-md border border-border bg-background/50 px-2 py-1 text-xs">
      <Icon className="size-3.5 shrink-0 text-muted-foreground" />
      <AttachmentPrimitive.Name />
    </AttachmentPrimitive.Root>
  );
}

// A sent image renders as a thumbnail that opens full-size in the same
// preview dialog as the composer tile. Until a reload the attachment still
// holds the pasted File; after one only the uploaded artifact's
// authenticated download URL remains, fetched only once the thumbnail scrolls
// into view so opening a long thread doesn't download every upload. An
// unloadable image keeps the chip.
function MessageImageAttachment() {
  const [broken, setBroken] = useState(false);
  const [placeholderRef, inView] = useInViewOnce<HTMLDivElement>();
  const name = useAuiState((s) => s.attachment.name);
  const contentType = useAuiState((s) => s.attachment.contentType);
  const file = useAuiState((s) => s.attachment.file);
  const downloadUrl = useAuiState((s) => s.attachment.content?.find((part) => part.type === 'file')?.data);
  const localPreview = useFilePreview(file, contentType);
  const uploaded = useAuthenticatedObjectUrl(file || !inView ? undefined : downloadUrl);
  const src = localPreview.kind === 'image' ? localPreview.src : uploaded.src;

  if (broken || uploaded.failed || (!file && !downloadUrl)) return <MessageAttachmentChip />;
  return (
    <AttachmentPrimitive.Root>
      {src ? (
        <AttachmentPreviewDialog preview={{ kind: 'image', src }} name={name}>
          <button type="button" aria-label={`Open ${name}`} className="block overflow-hidden rounded-xl ring-1 ring-border">
            <img src={src} alt={name} onError={() => setBroken(true)} className="block max-h-48 max-w-64 object-cover" />
          </button>
        </AttachmentPreviewDialog>
      ) : (
        <div ref={placeholderRef} role="status" aria-label={`Loading ${name}`} className="size-24 animate-pulse rounded-xl bg-secondary" />
      )}
    </AttachmentPrimitive.Root>
  );
}

const NarrationData = memo(function NarrationData({ data }: DataMessagePart) {
  const text = (data as { text?: string })?.text;
  // Cleared in place at `done` (see nativeThreadMessages `done` handler) rather
  // than removed from the parts array, so the stale live indicator disappears
  // without shifting sibling indices.
  if (!text) return null;
  return <p className="mb-2 text-sm text-muted-foreground italic">{text}</p>;
});

type OrchestrationConsultData = { agentId: string; displayName: string; rationale: string };

// Unlike NarrationData's flat text, this part is durable (never blanked at
// `done` — see nativeThreadMessages.ts) so the phase-1 checklist survives
// past run completion, matching how the specialist cards below it persist.
const SpecialistSelection = memo(function SpecialistSelection({ data }: DataMessagePart) {
  const consults = ((data as { consults?: OrchestrationConsultData[] } | undefined)?.consults) ?? [];
  // Selector must return a referentially stable value (useSyncExternalStore
  // requirement) -- select the content array itself, derive counts in a
  // memo, rather than returning a fresh { complete, total } object per call.
  const content = useAuiState((state) => state.message.content) as ThreadAssistantMessagePart[];
  const agentCalls = content.filter(
    (part): part is Extract<ThreadAssistantMessagePart, { type: 'tool-call' }> =>
      part.type === 'tool-call' && part.toolName.startsWith('Agent:'),
  );
  const complete = agentCalls.filter((call) => call.result !== undefined).length;
  const total = agentCalls.length;
  if (consults.length === 0) return null;
  return (
    <div className="mb-2 flex flex-col gap-2">
      <div>
        <Marker variant="separator" className="mb-1.5 text-xs font-semibold tracking-wide uppercase">
          <MarkerContent>Choosing the right specialists</MarkerContent>
        </Marker>
        <ul className="flex flex-col gap-1.5">
          {consults.map((consult) => (
            <li key={consult.agentId || consult.displayName} className="flex items-start gap-2 text-sm">
              <CheckIcon className="mt-0.5 size-3.5 shrink-0 text-emerald-600" aria-hidden="true" />
              <span>
                <span className="font-medium">{consult.displayName}</span>
                {consult.rationale && <span className="text-muted-foreground"> — {consult.rationale}</span>}
              </span>
            </li>
          ))}
        </ul>
      </div>
      {total > 0 && (
        <Marker variant="separator" className="text-xs font-semibold tracking-wide uppercase">
          <MarkerContent>Gathering insights — {complete}/{total} complete</MarkerContent>
        </Marker>
      )}
    </div>
  );
});

// Expectation-setting note for the currently-streaming assistant turn — one
// per message run, not a thread-level fixture (a thread-level version sat
// wherever it was mounted relative to the message list, drifting away from
// the turn it described once earlier turns existed above it). Static content
// only; the live elapsed-time clock is a separate concern that belongs in the
// action-bar row (see RunningActionBarTimer), not this descriptive note.
const RunningAnswerBanner = memo(function RunningAnswerBanner() {
  const title = 'Working on your answer';
  return (
    <Marker role="status" className="mb-2 items-start gap-2">
      <MarkerIcon className="mt-0.5">
        <Sparkle className="fill-primary text-primary" />
      </MarkerIcon>
      <MarkerContent className="flex flex-col gap-0.5">
        {/* Shimmer-over-text liveliness cue, same idiom as ReasoningTrigger/
            ToolGroupTrigger/ToolFallback's active-state shimmer: render the
            label normally, then an aria-hidden shimmering duplicate on top. */}
        <span className="relative inline-block w-fit font-medium text-foreground">
          <span>{title}</span>
          <span aria-hidden className="shimmer pointer-events-none absolute inset-0 motion-reduce:animate-none">
            {title}
          </span>
        </span>
        <span>
          Reviewing your question and gathering insights from the relevant specialist agents. You can
          follow its progress below and click any box to see what each specialist is working on.
        </span>
      </MarkerContent>
    </Marker>
  );
});

// The action bar's live counterpart to MessageTiming's settled duration --
// MessageTiming intentionally renders nothing while running (see its own
// comment) to leave this exact slot for a single ticking clock. Reads
// elapsed time from this message's own streaming metadata rather than a
// separate store lookup, since that's already tracked per message.
const RunningActionBarTimer = memo(function RunningActionBarTimer() {
  const streamStartTime = useAuiState((state) => state.message.metadata?.timing?.streamStartTime);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);
  const elapsed = streamStartTime === undefined ? null : Math.max(0, now - streamStartTime);
  const seconds = elapsed === null ? null : Math.floor(elapsed / 1000);
  const label = seconds === null ? 'Working…' : `Working · ${Math.floor(seconds / 60)}m ${(seconds % 60).toString().padStart(2, '0')}s`;
  return (
    <Marker>
      <MarkerIcon>
        <LoaderIcon className="animate-spin" />
      </MarkerIcon>
      <MarkerContent>{label}</MarkerContent>
    </Marker>
  );
});

// The orchestrator itself has paused this turn on a thread-level question
// (InterruptPanel renders the card below the thread). The message is settled
// so the running clock stops (see markLastAssistantPaused), but the turn is
// not finished: showing the completed-message ActionBar (copy / export /
// thumbs) here invites feedback on a partial answer. Static icon, no timer.
const WaitingForAnswerMarker = memo(function WaitingForAnswerMarker() {
  return (
    <Marker role="status">
      <MarkerIcon>
        <AlertCircleIcon className="text-primary" />
      </MarkerIcon>
      <MarkerContent>Waiting for your answer</MarkerContent>
    </Marker>
  );
});

// Action-bar slot for the LAST settled message only. Thread-level
// (orchestrator-owned) questions live in the runtime's interrupt slot, not in
// the message's parts, so the only way to know a settled-looking last message
// is actually mid-turn is to ask the runtime. Subscribing here, and only for
// the last message, keeps every historical ChatMessage off the interrupt
// context: otherwise each streaming update would re-render the whole
// transcript (the context value changes with the runtime slot).
const SettledTurnSlot = memo(function SettledTurnSlot() {
  const threadInterrupts = useAgenthubInterrupts();
  return threadInterrupts.length > 0 ? <WaitingForAnswerMarker /> : <ActionBar />;
});

// A delegated specialist paused for input. Rendered inside its own card so a
// second specialist asking in parallel cannot overwrite this one, reusing the
// thread-level panel rather than growing a second question renderer.
const SpecialistQuestion = memo(function SpecialistQuestion({ data }: DataMessagePart) {
  const { eventId, reason, payload, answered } = (data ?? {}) as {
    eventId?: string; reason?: string; payload?: Record<string, unknown>; answered?: boolean;
  };
  const currentQuestionId = useAuiState((state) => {
    for (const message of state.thread.messages) {
      if (message.role !== 'assistant') continue;
      for (const part of message.content) {
        if (part.type !== 'tool-call' || !part.toolName.startsWith('Agent:')) continue;
        for (const nested of part.messages ?? []) {
          if (nested.role !== 'assistant') continue;
          for (const item of nested.content) {
            if (item.type !== 'data' || item.name !== 'specialist_question') continue;
            const value = item.data as { eventId?: string; answered?: boolean };
            if (value.eventId && !value.answered) return value.eventId;
          }
        }
      }
    }
    return undefined;
  });
  // `resume_accepted` clears this card too, but only after a server round
  // trip. Dismissing locally on submit stops the answered question lingering.
  const [resolved, setResolved] = useState(false);
  if (answered || resolved || !eventId) return null;
  const queued = currentQuestionId !== undefined && currentQuestionId !== eventId;
  if (queued) {
    return <p className="text-xs text-muted-foreground">Queued until the current specialist question is answered.</p>;
  }
  return (
    <InterruptPanel
      compact
      onResolved={() => setResolved(true)}
      interrupt={{
        id: eventId,
        eventId,
        reason: reason ?? 'user_question',
        message: String(payload?.question ?? payload?.message ?? ''),
        parentToolCallId: typeof payload?.parent_tool_call_id === 'string' ? payload.parent_tool_call_id : undefined,
        metadata: { data: payload ?? {} },
      }}
    />
  );
});

function SubAgentMessage() {
  const role = useAuiState((state) => state.message.role);
  const running = useAuiState((state) => state.message.status?.type === 'running');
  const taskLabel = useContext(SpecialistTaskLabelContext);
  const actionBarSlot = useContext(SpecialistActionBarSlotContext);
  const Text = role === 'user' ? UserMarkdownText : MarkdownText;
  return (
    <div className={role === 'user' ? 'rounded-md bg-muted/50 px-3 py-2 text-xs' : 'text-sm'}>
      <p className="mb-1 text-xs font-medium text-muted-foreground">{role === 'user' ? taskLabel : 'Specialist work'}</p>
      <MessagePrimitive.Parts>
        {({ part }) => {
          switch (part.type) {
            case 'text':
              return <Text />;
            case 'source':
              return <Sources {...part} />;
            case 'file':
              return (
                <AssistantFilePart
                  type="data"
                  name="file"
                  data={{
                    filename: part.filename,
                    contentType: part.mimeType,
                    downloadUrl: part.data,
                    sizeBytes: undefined,
                  }}
                />
              );
            case 'data':
              return part.name === 'specialist_question' ? <SpecialistQuestion {...part} /> : part.dataRendererUI ?? null;
            default:
              return null;
          }
        }}
      </MessagePrimitive.Parts>
      {/* Same ActionBar the main response uses (Copy/Export/Feedback) —
          Feedback stays hidden here since nested specialist messages carry
          no metadata.custom.runId, leaving Copy/Export as the minimal set.
          Portaled into the dialog header's slot (see AgentToolCard) so it
          sits with the specialist name instead of at the end of the
          scrollable transcript. */}
      {role === 'assistant' && !running && actionBarSlot && createPortal(<ActionBar />, actionBarSlot)}
    </div>
  );
}

// What a specialist is doing right now, read from its own sub-transcript.
// The stream already carries every tool start and prose delta; the card just
// has to summarise it so a collapsed card is not a dead spinner.
function readSpecialistProgress(messages: readonly { role: string; content: readonly { type: string; toolName?: string; text?: string }[] }[]) {
  let activity: string | null = null;
  let answer: string | null = null;
  for (const message of messages) {
    if (message.role !== 'assistant') continue;
    for (const part of message.content) {
      if (part.type === 'tool-call' && part.toolName && !part.toolName.startsWith('Agent:')) activity = part.toolName;
      else if (part.type === 'reasoning') activity = 'thinking';
      else if (part.type === 'text' && part.text?.trim()) answer = part.text.trim();
    }
  }
  return { activity, answer };
}

const specialistStatusIcon = {
  running: LoaderIcon,
  complete: CheckIcon,
  incomplete: XCircleIcon,
  'requires-action': AlertCircleIcon,
} as const;

// assistant-ui's auto-scroll is bound to the thread viewport store; there is no
// primitive for a nested scroller. Runs on every render because a streaming
// card re-renders per delta, and only follows when the user is already at the
// bottom — scrolling up to read must not be yanked back.
function useStickToBottom() {
  const ref = useRef<HTMLDivElement>(null);
  const pinned = useRef(true);
  const scrollToTop = useCallback(() => {
    pinned.current = false;
    window.requestAnimationFrame(() => {
      const node = ref.current;
      if (node) node.scrollTop = 0;
    });
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (pinned.current) node.scrollTop = node.scrollHeight;
  });
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const onScroll = () => {
      pinned.current = node.scrollHeight - node.scrollTop - node.clientHeight < 24;
    };
    node.addEventListener('scroll', onScroll, { passive: true });
    return () => node.removeEventListener('scroll', onScroll);
  }, []);
  return [ref, scrollToTop] as const;
}

function AgentToolCard({
  part,
  runIsActive,
}: {
  part: Extract<ThreadAssistantMessagePart, { type: 'tool-call' }>;
  runIsActive?: boolean;
}) {
  const messageRunning = useAuiState((state) => state.message.status?.type === 'running');
  const shouldCollapse = runIsActive ?? messageRunning;
  const messages = part.messages ?? [];
  const pendingQuestion = messages.some((message) => message.role === 'assistant'
    && message.content.some((item) => item.type === 'data'
      && item.name === 'specialist_question'
      && !(item.data as { answered?: boolean }).answered));
  const statusType = part.isError
    ? 'incomplete' as const
    : pendingQuestion ? 'requires-action' as const
    : part.result === undefined && messageRunning ? 'running' as const : 'complete' as const;
  const { activity, answer } = readSpecialistProgress(messages);
  const rationale = typeof part.args?.rationale === 'string' ? part.args.rationale : '';
  const phase = part.args?.phase === 'bid' ? 'bid' : 'assignment';
  const taskLabel = phase === 'bid' ? 'Capability check' : 'Substantive assignment';
  const Icon = specialistStatusIcon[statusType];
  const [open, setOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogScrollRef, scrollDialogToTop] = useStickToBottom();
  // Callback ref (not useRef) so its state update re-renders SubAgentMessage
  // once the header's slot node actually exists to portal into.
  const [actionBarSlot, setActionBarSlot] = useState<HTMLDivElement | null>(null);
  const specialistName = part.toolName.slice('Agent:'.length);
  // Four distinct states, not one spinner: dispatching → working → needs
  // input → answered. The answer preview is a stand-in for content the user
  // cannot see, so it goes away once the card is open. It is the reply's
  // first plain sentence (bid cards: the RELEVANCE verdict), not its raw
  // first markdown line -- see summarizeSpecialistAnswer.
  const line = statusType === 'incomplete' ? 'could not finish'
    : statusType === 'requires-action' ? 'needs your input'
    : statusType === 'running' ? (activity ?? 'briefing the specialist')
    : open ? 'answered' : (summarizeSpecialistAnswer(answer, phase) ?? 'done');
  // A question the user cannot see is a stalled turn. Opening on arrival keeps
  // it collapsible afterwards, unlike a fully controlled card.
  useEffect(() => {
    if (pendingQuestion) setOpen(true);
  }, [pendingQuestion]);
  useEffect(() => {
    if (!shouldCollapse && !pendingQuestion) setOpen(false);
  }, [shouldCollapse, pendingQuestion]);
  // A citation link in the synthesized answer lives outside this part's
  // context, so it can't open this Dialog directly -- it requests this
  // toolCallId through the shared store instead; this card is the one thing
  // that both owns the Dialog and knows its own toolCallId.
  const requestedToolCallId = useSpecialistDialogStore((state) => state.requestedToolCallId);
  const clearDialogRequest = useSpecialistDialogStore((state) => state.clear);
  useEffect(() => {
    if (requestedToolCallId !== part.toolCallId) return;
    scrollDialogToTop();
    setOpen(true);
    setDialogOpen(true);
    clearDialogRequest();
  }, [requestedToolCallId, part.toolCallId, scrollDialogToTop, clearDialogRequest]);
  return (
    <ToolFallback.Root
      id={specialistCardAnchorId(part.toolCallId)}
      open={open}
      onOpenChange={setOpen}
      className={`my-2 rounded-lg border bg-card px-3 ${pendingQuestion ? 'border-primary' : 'border-border'}`}
    >
      <CollapsibleTrigger className="flex w-full items-center gap-2 py-2 text-left text-sm">
        <Icon className={`size-4 shrink-0 ${statusType === 'running' ? 'animate-spin [animation-duration:0.6s]' : ''}`} />
        <span className="shrink-0 font-medium">{specialistName}</span>
        <span className="shrink-0 text-xs text-muted-foreground">{taskLabel}</span>
        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground" aria-live="polite">{line}</span>
        <ToolFallback.Duration className="shrink-0" />
        <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]/tool-fallback-root:rotate-180" />
      </CollapsibleTrigger>
      <ToolFallback.Content aria-live="polite">
        {rationale ? <p className="pb-2 text-xs italic text-muted-foreground">{rationale}</p> : null}
        {/* Clamped preview, not an inline scroller -- matches the LangChain
            subagent-streaming reference pattern (line-clamp + click-through
            to a full view) instead of a fixed-height overflow-y-auto box. */}
        {answer && (
          <p className="line-clamp-6 pb-2 text-sm whitespace-pre-wrap text-foreground">{answer}</p>
        )}
        <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={() => setDialogOpen(true)}>
          View full response
        </Button>
      </ToolFallback.Content>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <div className="flex items-center gap-3 pr-8">
              <DialogTitle>{specialistName}</DialogTitle>
              <div ref={setActionBarSlot} className="flex items-center" />
            </div>
            {rationale && <DialogDescription>{rationale}</DialogDescription>}
          </DialogHeader>
          {/* shadcn's own "Scrollable Content" dialog pattern: a plain
              overflow-y-auto div, not a nested ScrollArea. */}
          <div ref={dialogScrollRef} className="-mx-4 max-h-[60vh] overflow-y-auto px-4">
            <SpecialistTaskLabelContext.Provider value={taskLabel}>
              <SpecialistActionBarSlotContext.Provider value={actionBarSlot}>
                <MessagePartPrimitive.Messages>
                  {() => <SubAgentMessage />}
                </MessagePartPrimitive.Messages>
              </SpecialistActionBarSlotContext.Provider>
            </SpecialistTaskLabelContext.Provider>
          </div>
        </DialogContent>
      </Dialog>
    </ToolFallback.Root>
  );
}

// Registered per accessible agent so the card is presented standalone —
// outside the orchestrator's collapsed tool group (see SpecialistToolUIs).
// Reads the part from context because a registered renderer's props omit the
// nested `messages` this card is built from.
export const SpecialistCard: ToolCallMessagePartComponent = () => {
  const part = useAuiState((state) => state.part);
  const runIsActive = useContext(MessageRunContext);
  if (part.type !== 'tool-call') return null;
  return <AgentToolCard part={part} runIsActive={runIsActive} />;
};

const auxiliaryTriggerClassName =
  'text-sm font-normal leading-relaxed text-muted-foreground hover:text-foreground';

const auxiliaryContentClassName = 'text-sm leading-relaxed text-muted-foreground';

function ToolCallGroup({ children, active, count, defaultOpen, runIsActive = false }: {
  children: ReactNode;
  active: boolean;
  count: number;
  defaultOpen: boolean;
  runIsActive?: boolean;
}) {
  const [open, setOpen] = useCollapseAfterRun(runIsActive, defaultOpen);
  const visibleChildren = Children.toArray(children).filter(Boolean);
  if (visibleChildren.length === 0) return null;
  return (
    <ToolGroupRoot variant="ghost" open={open} onOpenChange={setOpen} className="my-1">
      <ToolGroupTrigger
        count={count}
        active={active}
        className={auxiliaryTriggerClassName}
      />
      <ToolGroupContent className={auxiliaryContentClassName}>{visibleChildren}</ToolGroupContent>
    </ToolGroupRoot>
  );
}

function useCollapseAfterRun(runIsActive: boolean, defaultOpen: boolean) {
  const [open, setOpen] = useState(defaultOpen);
  const wasRunning = useRef(runIsActive);

  useEffect(() => {
    if (wasRunning.current && !runIsActive) setOpen(false);
    wasRunning.current = runIsActive;
  }, [runIsActive]);

  return [open, setOpen] as const;
}

function MessageReasoningGroup({
  children,
  active,
  runIsActive,
}: {
  children: ReactNode;
  active: boolean;
  runIsActive: boolean;
}) {
  const [open, setOpen] = useCollapseAfterRun(runIsActive, active);
  return (
    <ReasoningRoot variant="ghost" open={open} onOpenChange={setOpen} className="my-1">
      <ReasoningTrigger active={active} />
      <ReasoningContent aria-busy={active}>
        <ReasoningText>{children}</ReasoningText>
      </ReasoningContent>
    </ReasoningRoot>
  );
}

function hasUserText(content: readonly { type: string; text?: string }[]): boolean {
  return content.some((part) => part.type === 'text' && Boolean(part.text?.trim()));
}

// Reveals on hover/focus like the assistant ActionBar; user messages have
// nothing else to show there (no export/feedback), so this skips the shared
// ActionBar component and uses the native primitive directly.
function UserCopyButton() {
  return (
    <ActionBarPrimitive.Root className="opacity-0 transition-opacity group-focus-within/message:opacity-100 group-hover/message:opacity-100">
      <ActionBarPrimitive.Copy
        aria-label="Copy message"
        className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
      >
        <AuiIf condition={(s) => s.message.isCopied}>
          <CheckIcon className="size-4" />
        </AuiIf>
        <AuiIf condition={(s) => !s.message.isCopied}>
          <ClipboardIcon className="size-4" />
        </AuiIf>
      </ActionBarPrimitive.Copy>
    </ActionBarPrimitive.Root>
  );
}

export const ChatMessage: FC<{ agentId?: string }> = ({ agentId }) => {
  const messageRunning = useAuiState((state) => state.message.status?.type === 'running');
  const messageRole = useAuiState((state) => state.message.role);
  const messageContent = useAuiState((state) => state.message.content);
  const isLast = useAuiState((state) => state.message.isLast);
  // Downloadable files are terminal assistant output. Keep their native
  // message order for replay, but present all file cards after the answer so
  // they do not interrupt reasoning, tools, visuals, or prose.
  const assistantFileParts = messageContent.filter((part) => part.type === 'file');
  const hasSpecialists = messageContent.some((part) => part.type === 'tool-call' && part.toolName.startsWith('Agent:'));
  // A mutable "have we shown it yet" flag is unsafe here (confirmed by a
  // regression): it's pure order-dependent state, fragile against however
  // many times GroupedParts' render prop actually runs. Reference equality
  // against messageContent doesn't work either -- GroupedParts hands leaf
  // parts through as a distinct "EnrichedPartState" wrapper object, never
  // the same instance as what a separate useAuiState selector reads. Content
  // comparison is the only pure, subscription-independent signal available.
  const firstTextPart = messageContent.find((part) => part.type === 'text');
  const firstTextValue = firstTextPart?.type === 'text' ? firstTextPart.text : undefined;

  return (
    <MessagePrimitive.Root
      data-role={messageRole}
      className="group/message relative mx-auto flex w-full max-w-[70rem] flex-col py-2"
    >
      <AuiIf condition={(s) => s.message.role === 'user'}>
        <div className="flex flex-col items-end gap-1">
          <div className="flex flex-row flex-wrap justify-end gap-1.5">
            <MessagePrimitive.Attachments components={{ Image: MessageImageAttachment, Attachment: MessageAttachmentChip }} />
          </div>
          <QuoteBlock />
          {/* An attachment-only turn has no text for a bubble — just its timestamp. */}
          <AuiIf condition={(s) => hasUserText(s.message.content)}>
            <div
              data-slot="user-message-bubble"
              className="max-w-[80%] rounded-2xl bg-secondary px-4 py-2.5 break-words whitespace-pre-wrap text-secondary-foreground"
            >
              <MessagePrimitive.Parts
                components={{
                  Text: UserMarkdownText,
                  data: { Fallback: () => null },
                }}
              />
              <MessageTimestamp className="block text-right" />
            </div>
          </AuiIf>
          <AuiIf condition={(s) => !hasUserText(s.message.content)}>
            <MessageTimestamp />
          </AuiIf>
          <UserCopyButton />
        </div>
      </AuiIf>

      <AuiIf condition={(s) => s.message.role === 'assistant'}>
        <MessageRunContext.Provider value={messageRunning}>
          <div className="flex flex-col">
          {/* NOTE for concurrent agent editing this file: `prose` was a no-op
              here (no @tailwindcss/typography plugin installed) — replaced
              with `text-sm` since MarkdownComponents.tsx now supplies its own
              full typography scale/spacing for headings, lists, code, tables. */}
          <div className="max-w-none break-words text-sm text-foreground">
            {/* This banner describes Argus's own multi-specialist orchestration
                flow ("gathering insights from the relevant specialist
                agents") -- a plain single-agent Claude-runtime turn has no
                specialists to gather from, so it must never show here. */}
            {agentId === 'argus' && (
              <AuiIf condition={(s) => s.message.status?.type === 'running'}>
                <RunningAnswerBanner />
              </AuiIf>
            )}
            <MessagePrimitive.GroupedParts
              indicator="empty"
              groupBy={groupPartByType({
                reasoning: ['group-chainOfThought', 'group-reasoning'],
                'tool-call': ['group-chainOfThought', 'group-tool'],
                // Standalone tool UIs (specialist cards, artifacts) are their own block —
                // without this key the registry flag is ignored and they group like a trace.
                'standalone-tool-call': [],
              })}
            >
              {({ part, children }) => {
                switch (part.type) {
                  case 'group-chainOfThought':
                    return <div className="my-1 flex flex-col gap-1">{children}</div>;
                  case 'group-reasoning': {
                    const running = part.status.type === 'running';
                    return (
                      <MessageReasoningGroup active={running} runIsActive={messageRunning}>
                        {children}
                      </MessageReasoningGroup>
                    );
                  }
                  case 'group-tool': {
                    const active = part.status.type === 'running';
                    const firstTool = part.indices
                      .map((index) => messageContent[index])
                      .find((candidate) => candidate?.type === 'tool-call');
                    const groupId = firstTool?.type === 'tool-call' ? firstTool.toolCallId : String(part.indices[0] ?? 0);
                    return (
                      <ToolCallGroup
                        key={groupId}
                        active={active}
                        count={part.indices.length}
                        defaultOpen={false}
                        runIsActive={messageRunning}
                      >
                        {children}
                      </ToolCallGroup>
                    );
                  }
                  case 'text': {
                    // "Response" separates specialist activity from Argus's
                    // own synthesized answer -- only the first visible answer
                    // text gets it, not every streamed chunk.
                    const showSeparator = hasSpecialists && firstTextValue !== undefined && part.text === firstTextValue;
                    return (
                      <>
                        {showSeparator && (
                          <Marker variant="separator" className="mb-2 text-xs font-semibold tracking-wide uppercase">
                            <MarkerContent>Response</MarkerContent>
                          </Marker>
                        )}
                        <MarkdownText />
                      </>
                    );
                  }
                  case 'reasoning':
                    return <Reasoning {...part} />;
                  case 'image':
                    return <AuthenticatedImagePart {...part} />;
                  case 'file':
                    return null;
                  case 'tool-call':
                    return part.toolUI ?? (part.toolName.startsWith('Agent:')
                      ? <AgentToolCard part={part} runIsActive={messageRunning} />
                      : <ToolFallback {...part} />);
                  case 'source':
                    return <Sources {...part} />;
                  case 'data':
                    if (part.name === 'specialist_selection') return <SpecialistSelection {...part} />;
                    if (part.name === 'claude_narration' || part.name === 'orchestration_preamble' || part.name === 'argus_narration') return <NarrationData {...part} />;
                    return part.dataRendererUI ?? null;
                  case 'generative-ui':
                    return <MessagePrimitive.GenerativeUI components={generativeUiComponents} />;
                  case 'indicator':
                    return null;
                  default:
                    return null;
                }
              }}
            </MessagePrimitive.GroupedParts>
            {assistantFileParts.map((part, index) => (
              <AssistantFilePart
                key={`${part.filename ?? 'file'}-${index}`}
                type="data"
                name="file"
                data={{
                  filename: part.filename,
                  contentType: part.mimeType,
                  downloadUrl: part.data,
                  sizeBytes: undefined,
                }}
              />
            ))}
          </div>
          <RunStatusBadge />
          {messageRunning ? <RunningActionBarTimer /> : isLast ? <SettledTurnSlot /> : <ActionBar />}
          </div>
        </MessageRunContext.Provider>
      </AuiIf>
    </MessagePrimitive.Root>
  );
};
